import React from 'react';
import { FiX, FiMapPin, FiClock, FiCornerUpRight, FiPrinter } from 'react-icons/fi';
import { FaStar } from 'react-icons/fa';
import { Shop } from '../../lib/supabase/api/shopService';
import IconWrapper from '../print-steps/IconWrapper';

// Import the same drop icons used in ShopCard
import blackDropIcon from '../../assets/icons/black_drop.png';
import colorsDropIcon from '../../assets/icons/colors_drop.png';

interface ShopDetailsModalProps {
  shop: Shop;
  isOpen: boolean;
  onClose: () => void;
}

const ShopDetailsModal: React.FC<ShopDetailsModalProps> = ({ shop, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  const shopAddress = shop.address || `${shop.location}, University Campus`;
  
  // Use the same image function as ShopCard
  const getShopImageUrl = () => {
    if (shop.shop_image_path) {
      return `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/shop_images/${shop.shop_image_path}`;
    }
    return '/print-shop.jpg';
  };
  
  // Generate directions URL for Google Maps
  const getDirectionsUrl = () => {
    if (shop.lat && shop.lon) {
      return `https://www.google.com/maps?q=${shop.lat},${shop.lon}`;
    }
    return `https://www.google.com/maps?q=${encodeURIComponent(shopAddress)}`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-3 sm:p-5 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-xs sm:max-w-sm md:max-w-md max-h-[60vh] md:max-h-[55vh] overflow-hidden shadow-xl animate-scaleIn flex flex-col"
        onClick={e => e.stopPropagation()} // Prevent closing when clicking inside
        style={{
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          animation: 'scaleIn 0.2s ease-out'
        }}
      >
        {/* Header - made more compact */}
        <div className="p-3 pb-2 bg-white z-10 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-500 p-1.5 rounded-lg">
                <IconWrapper icon={FiPrinter} className="h-4 w-4 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-xs sm:text-base">{shop.name}</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{shop.location}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1.5"
              aria-label="Close details"
            >
              <IconWrapper icon={FiX} className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Scrollable content area with visible scrollbar */}
        <div 
          className="flex-1 overflow-y-auto"
          style={{ 
            overflowY: 'auto', 
            scrollbarWidth: 'thin',
            scrollbarColor: '#D1D5DB #F3F4F6'
          }}
        >
          <div className="px-4 py-2 flex justify-between items-center">
            <div className="flex items-center">
              <IconWrapper icon={FaStar} className="text-yellow-400 h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
              <span className="text-[10px] sm:text-sm font-medium">3.9</span>
              <span className="text-[10px] sm:text-sm text-gray-500 ml-1.5">(42 reviews)</span>
            </div>          
          </div>
          
          {/* Shop Image - display complete image with increased size (125%) */}
          <div className="px-4 py-4 flex justify-center">
              <img 
                src={getShopImageUrl()} 
                alt={shop.name}
                className="w-auto h-auto max-w-full max-h-40 sm:max-h-50 object-contain rounded-lg" 
              />
          </div>
          
          {/* Location - more compact */}
          <div className="px-4 py-2 flex items-start space-x-2">
            <div className="bg-gray-50 p-2 rounded-lg flex items-start space-x-3 w-full">
              <div className="text-gray-600 mt-0.5">
                <IconWrapper icon={FiMapPin} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
              <div className="text-[10px] sm:text-sm flex-1">
                <div className="text-gray-800">{shopAddress}</div>
              </div>
            </div>
          </div>
          
          {/* Opening Hours - more compact */}
          <div className="px-4 py-2 flex items-center">
            <div className="bg-green-50 p-2 rounded-lg flex items-center space-x-3 w-full">
              <div className="text-green-600">
                <IconWrapper icon={FiClock} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
              <div className="text-[10px] sm:text-sm font-medium">{shop.open_hours}</div>
            </div>
          </div>
          
          {/* Available Services - more compact */}
          <div className="p-4 pb-2">
            <div className="text-[10px] sm:text-sm font-medium mb-2">Available Services</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {['Printing', 'Binding','Scanning'].map((service, index) => (
                <div key={index} className="flex items-center bg-gray-50 rounded-lg p-1.5 sm:p-2.5 space-x-2">
                  <div className="bg-blue-50 p-0.5 sm:p-1 rounded-lg">
                    <IconWrapper icon={FiPrinter} className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-600" />
                  </div>
                  <span className="text-[10px] sm:text-sm">{service}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Pricing Details - show tiers */}
          <div className="p-4 pt-2 pb-4">
            <div className="text-[10px] sm:text-sm font-medium mb-2">Pricing Details</div>
            <div className="space-y-3">
              {/* Check if we have tiered pricing for any paper type */}
              {shop.pricing && Object.keys(shop.pricing).length > 0 ? (
                Object.entries(shop.pricing).map(([paperType, tiers]) => {
                  // Check if it's tier array or legacy single pricing
                  const isTierArray = Array.isArray(tiers);
                  
                  return (
                    <div key={paperType} className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-semibold text-gray-700 mb-2">{paperType} Paper</div>
                      {isTierArray && tiers.length > 0 ? (
                        <div className="space-y-2">
                          {tiers.map((tier: any, index: number) => (
                            <div key={index} className="text-[10px] sm:text-xs">
                              <div className="font-medium text-gray-600 mb-1">
                                {tier.min_pages}-{tier.max_pages || '∞'} pages
                              </div>
                              <div className="flex justify-between pl-3">
                                <div className="flex items-center space-x-1.5">
                                  <img src={blackDropIcon} alt="B&W" className="w-3 h-3" />
                                  <span>B&W</span>
                                </div>
                                <span className="font-medium">₹{tier.user_monochrome_cost.toFixed(2)}/page</span>
                              </div>
                              <div className="flex justify-between pl-3 mt-1">
                                <div className="flex items-center space-x-1.5">
                                  <img src={colorsDropIcon} alt="Color" className="w-3 h-3" />
                                  <span>Color</span>
                                </div>
                                <span className="font-medium">₹{tier.user_color_cost.toFixed(2)}/page</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Legacy single pricing format
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <img src={blackDropIcon} alt="B&W" className="w-3.5 h-3.5" />
                              <span className="text-[10px] sm:text-sm">B&W</span>
                            </div>
                            <span className="text-[10px] sm:text-sm font-medium">₹{(tiers as any).user_monochrome_cost || '2.5'}/page</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <img src={colorsDropIcon} alt="Color" className="w-3.5 h-3.5" />
                              <span className="text-[10px] sm:text-sm">Color</span>
                            </div>
                            <span className="text-[10px] sm:text-sm font-medium">₹{(tiers as any).user_color_cost || '10.0'}/page</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                // Fallback to shop-level pricing
                <div className="space-y-2 sm:space-y-3 bg-gray-50 rounded-lg p-2 sm:p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <img src={blackDropIcon} alt="Black & White" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="text-[10px] sm:text-sm">Black & White</span>
                    </div>
                    <span className="text-[10px] sm:text-sm font-medium">₹{shop.monochrome_cost || '2.5'} per page</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <img src={colorsDropIcon} alt="Color Print" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="text-[10px] sm:text-sm">Color Print</span>
                    </div>
                    <span className="text-[10px] sm:text-sm font-medium">₹{shop.color_cost || '10.0'} per page</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer - more compact */}
        <div className="flex p-3 pt-2 sm:p-3.5 sm:pt-2.5 justify-between bg-white border-t border-gray-100">
          <a
            href={getDirectionsUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 mr-2 inline-flex justify-center items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 text-white text-[10px] sm:text-xs rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <IconWrapper icon={FiMapPin} className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5" />
            <span>Get Directions</span>
          </a>
          <button
            onClick={onClose}
            className="flex-1 inline-flex justify-center items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 text-gray-700 text-[10px] sm:text-xs rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopDetailsModal;
